/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { useLog } from "src/hooks/use-log";
import { ColorEnum } from "src/utils/color-log.util";
import { appLogPrefixes } from "./app-config";
 
const azureB2cAuthorityServer = `https://b2clogin.cdph.ca.gov/${process.env.REACT_APP_AZURE_B2C_AUTHORITY_DOMAIN}.onmicrosoft.com`;  //hardcoded across envs
const azureB2cAuthUri = `https://${process.env.REACT_APP_AZURE_B2C_AUTHORITY_DOMAIN}.onmicrosoft.com/${process.env.REACT_APP_AZURE_B2C_CLIENT_API_ID}`.trim();
const azureB2cAuthScope  = `${azureB2cAuthUri}/${process.env.REACT_APP_AZURE_B2C_APICONFIG_B2C_SCOPE}`.trim();
const postLogoutRedirectUri = `${process.env.REACT_APP_FRONTEND_CAIR_HUB_URL}${process.env.REACT_APP_AZURE_B2C_POST_LOGOUT_REDIRECT_ROUTE}`.trim()
	 
/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: process.env.REACT_APP_AZURE_B2C_POLICIES_SIGNIN,
		forgotPassword: process.env.REACT_APP_AZURE_B2C_POLICIES_PASSWORD_RESET,
    },
    authorities: {
        signUpSignIn: {
            authority: `${azureB2cAuthorityServer}/${process.env.REACT_APP_AZURE_B2C_POLICIES_SIGNIN}`  
        }, 
    },
    authorityDomain: `b2clogin.cdph.ca.gov`
}

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
	auth: {
		clientId: process.env.REACT_APP_AZURE_B2C_CLIENT_ID,
		authority: b2cPolicies.authorities.signUpSignIn.authority,
        knownAuthorities: [b2cPolicies.authorityDomain], 
		redirectUri: process.env. REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI,
		postLogoutRedirectUri: postLogoutRedirectUri,
		validateAuthority: process.env.REACT_APP_AZURE_B2C_VALIDATE_AUTHORITY, 
	},
	cache: {
		cacheLocation: process.env.REACT_APP_AZURE_B2C_STORAGE_TYPE, // This configures where your cache will be stored
		storeAuthStateInCookie: true, // IMPORTANT: Keep this as true process.env.REACT_APP_AZURE_B2C_SET_AUTH_STATE_IN_COOKIE
	},
	system: {
		loggerOptions: {
			loggerCallback: (level, message, containsPii) => {

				if (containsPii) {
					useLog(`MSAL logger: Disabling logging due to PII...`, ColorEnum.WARNING, appLogPrefixes.msalAuth); 
					return;
				}

				// Only if display if verbose logging is enabled.
				if(process.env.REACT_APP_AZURE_B2C_ENABLE_LOG === "true"){
					switch (level) {
						case LogLevel.Error:
							useLog(`MSAL logger: ${message}`, ColorEnum.ERROR, appLogPrefixes.msalAuth); 
							return;
						case LogLevel.Info:
							useLog(`MSAL logger: ${message}`, ColorEnum.INFO, appLogPrefixes.msalAuth); 
							return;
						case LogLevel.Verbose:
							useLog(`MSAL logger: ${message}`, ColorEnum.VERBOSE, appLogPrefixes.msalAuth);  
							return;
						case LogLevel.Warning:
							useLog(`MSAL logger: ${message}`, ColorEnum.WARNING, appLogPrefixes.msalAuth);   
							return;
						default:
							return;
					}
				} 
			},
		},
	},
};


/*
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */

export const loginRequest = {
	scopes: [azureB2cAuthScope]   
};

 
/**
 * Enter here the coordinates of your web API and scopes for access token request
 * The current application coordinates were pre-registered in a B2C tenant.
 */
export const apiConfig = {
    scopes: [azureB2cAuthScope],
    uri: azureB2cAuthUri
};
 
 